import { useCallback } from 'react';
import { getNearestNode } from '../utils/node';

// ==========================================
// ENHANCED TYPES FOR CLARITY ANALYSIS
// ==========================================

export interface ClarityElementContext {
  // Element identification
  tag: string;
  role: string | null;
  semanticType:
    | 'heading'
    | 'button'
    | 'link'
    | 'input'
    | 'text'
    | 'image'
    | 'container'
    | 'navigation'
    | 'form'
    | 'list'
    | 'table'
    | 'other';

  // Element attributes (NEW)
  attributes: {
    id?: string;
    className?: string;
    href?: string; // For links
    src?: string; // For images/media
    alt?: string; // For images
    type?: string; // For inputs/buttons
    value?: string; // For inputs
    placeholder?: string; // For inputs
    disabled?: boolean;
    required?: boolean;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    dataAttributes: Record<string, string>; // data-* attributes
  };

  // Visual characteristics
  text: string | null;
  visualDescription: string;
  size: {
    width: number;
    height: number;
    area: number;
    sizeCategory: 'small' | 'medium' | 'large' | 'extra-large';
  };

  // Layout context
  position: {
    x: number;
    y: number;
    viewportPosition: 'top' | 'middle' | 'bottom';
    horizontalPosition: 'left' | 'center' | 'right';
    isVisible: boolean;
    isAboveFold: boolean;
    zIndex: number;
  };

  // Interaction context
  interactionType: 'clickable' | 'focusable' | 'interactive' | 'static';
  purpose: string;
  accessibility: {
    hasAccessibleName: boolean;
    hasDescription: boolean;
    isFocusable: boolean;
    tabIndex: number | null;
    ariaExpanded?: boolean;
    ariaPressed?: boolean;
    ariaChecked?: boolean;
    ariaHidden?: boolean;
  };

  // Enhanced visual styling
  styling: {
    backgroundColor: string | null;
    textColor: string | null;
    fontSize: string | null;
    fontWeight: string | null;
    fontFamily: string | null;
    lineHeight: string | null;
    letterSpacing: string | null;
    textTransform: string | null;
    hasBackground: boolean;
    hasBorder: boolean;
    borderRadius: string | null;
    borderColor: string | null;
    borderWidth: string | null;
    boxShadow: string | null;
    opacity: string | null;
    transform: string | null;
    transition: string | null;
    cursor: string | null;
    display: string;
    position: string;
    overflow: string | null;
    margin: string | null;
    padding: string | null;
  };

  // Context within page
  pageContext: {
    section: string | null;
    nearbyElements: Array<{
      relationship: 'sibling' | 'parent' | 'child';
      tag: string;
      text: string | null;
      semanticType: string;
      distance?: number;
    }>;
    hierarchyLevel: number;
    contentType: 'navigation' | 'content' | 'form' | 'media' | 'decoration';
    isInForm: boolean;
    formContext?: {
      formMethod: string;
      formAction: string;
      isRequired: boolean;
      fieldType: string;
    };
    isInList: boolean;
    listContext?: {
      listType: 'ordered' | 'unordered' | 'definition';
      itemIndex: number;
      totalItems: number;
    };
  };

  // Business context
  businessContext: {
    likely_purpose: string;
    user_action: string | null;
    content_category: 'primary' | 'secondary' | 'auxiliary';
    designPattern?:
      | 'cta'
      | 'navigation'
      | 'form-field'
      | 'card'
      | 'badge'
      | 'menu'
      | 'tooltip'
      | 'modal'
      | 'other';
    brandingElements?: string[];
  };

  // Performance and technical context (NEW)
  technicalContext: {
    loadingState?: 'loaded' | 'loading' | 'error';
    hasEventListeners: boolean;
    isLazyLoaded?: boolean;
    hasAnimation: boolean;
    responsiveClass?: string[];
    frameworkClasses?: string[]; // Bootstrap, Tailwind, etc.
  };
}

export interface ClarityAnalysisData {
  elementContext: ClarityElementContext;
  relatedElements: Array<{
    relationship: 'parent' | 'sibling' | 'child' | 'nearby';
    description: string;
    element: ClarityElementContext;
  }>;
  pageMetadata: {
    title: string;
    url: string;
    pageType: string;
    viewport: {
      width: number;
      height: number;
      devicePixelRatio: number;
    };
    colorScheme?: 'light' | 'dark' | 'auto';
    language?: string;
  };
}

export interface EnhancedElementProperties {
  tagName?: string;
  className?: string;
  id?: string;
  textContent?: string;
  computedStyles?: Record<string, string> | CSSStyleDeclaration;
  position?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  // Enhanced attributes
  attributes?: {
    href?: string;
    src?: string;
    alt?: string;
    type?: string;
    value?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    ariaLabel?: string;
    dataAttributes?: Record<string, string>;
  };
  // Enhanced context
  contextualInfo?: {
    semanticType: string;
    purpose: string;
    interactionType: string;
    pageSection: string | null;
    businessContext: string;
    contentCategory: string;
    nearbyElements: string[];
    userAction: string | null;
    sizeCategory: string;
    viewportPosition: string;
    accessibility?: {
      hasAccessibleName: boolean;
      isFocusable: boolean;
      ariaExpanded?: boolean;
    };
    formContext?: {
      isInForm: boolean;
      fieldType?: string;
      isRequired?: boolean;
    };
    designPattern?: string;
    technicalNotes?: string[];
  };
}

// ==========================================
// ENHANCED ELEMENT CONTEXT ANALYZER HOOK
// ==========================================

const useElementContextAnalyzer = () => {
  // Enhanced semantic type detection
  const getSemanticType = useCallback(
    (el: Element): ClarityElementContext['semanticType'] => {
      const tag = el.tagName.toLowerCase();
      const role = el.getAttribute('role');
      const type = el.getAttribute('type');

      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) return 'heading';
      if (
        tag === 'button' ||
        role === 'button' ||
        (tag === 'input' && type === 'button')
      )
        return 'button';
      if (tag === 'a' || role === 'link') return 'link';
      if (['input', 'select', 'textarea'].includes(tag)) return 'input';
      if (['img', 'svg', 'canvas', 'video', 'audio', 'picture'].includes(tag))
        return 'image';
      if (['nav', 'menu'].includes(tag) || role === 'navigation')
        return 'navigation';
      if (tag === 'form' || role === 'form') return 'form';
      if (['ul', 'ol', 'dl'].includes(tag) || role === 'list') return 'list';
      if (tag === 'table' || role === 'table') return 'table';
      if (['p', 'span', 'div'].includes(tag) && el.textContent?.trim())
        return 'text';
      if (
        ['section', 'article', 'main', 'header', 'footer', 'aside'].includes(
          tag
        )
      )
        return 'container';
      return 'other';
    },
    []
  );

  // Extract comprehensive attributes
  const getElementAttributes = useCallback((el: Element) => {
    const attrs: ClarityElementContext['attributes'] = {
      dataAttributes: {},
    };

    // Basic attributes
    if (el.id) attrs.id = el.id;
    if (el.className) attrs.className = el.className;
    if (el.getAttribute('href')) attrs.href = el.getAttribute('href')!;
    if (el.getAttribute('src')) attrs.src = el.getAttribute('src')!;
    if (el.getAttribute('alt')) attrs.alt = el.getAttribute('alt')!;
    if (el.getAttribute('type')) attrs.type = el.getAttribute('type')!;
    if (el.getAttribute('value')) attrs.value = el.getAttribute('value')!;
    if (el.getAttribute('placeholder'))
      attrs.placeholder = el.getAttribute('placeholder')!;
    if (el.hasAttribute('disabled')) attrs.disabled = true;
    if (el.hasAttribute('required')) attrs.required = true;
    if (el.getAttribute('aria-label'))
      attrs.ariaLabel = el.getAttribute('aria-label')!;
    if (el.getAttribute('aria-describedby'))
      attrs.ariaDescribedBy = el.getAttribute('aria-describedby')!;

    // Collect data attributes
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.startsWith('data-')) {
        attrs.dataAttributes[attr.name] = attr.value;
      }
    });

    return attrs;
  }, []);

  // Enhanced accessibility analysis
  const getAccessibilityInfo = useCallback((el: Element) => {
    const ariaLabel = el.getAttribute('aria-label');
    const ariaLabelledBy = el.getAttribute('aria-labelledby');
    const alt = el.getAttribute('alt');
    const title = el.getAttribute('title');
    const textContent = el.textContent?.trim();

    const hasAccessibleName = !!(
      ariaLabel ||
      ariaLabelledBy ||
      alt ||
      title ||
      textContent
    );
    const hasDescription = !!(
      el.getAttribute('aria-describedby') || el.getAttribute('title')
    );
    const tabIndex = el.getAttribute('tabindex');
    const isFocusable =
      tabIndex !== null ||
      ['a', 'button', 'input', 'select', 'textarea'].includes(
        el.tagName.toLowerCase()
      );

    return {
      hasAccessibleName,
      hasDescription,
      isFocusable,
      tabIndex: tabIndex ? parseInt(tabIndex) : null,
      ariaExpanded: el.getAttribute('aria-expanded') === 'true',
      ariaPressed: el.getAttribute('aria-pressed') === 'true',
      ariaChecked: el.getAttribute('aria-checked') === 'true',
      ariaHidden: el.getAttribute('aria-hidden') === 'true',
    };
  }, []);

  // Enhanced styling extraction
  const getEnhancedStyling = useCallback((el: Element) => {
    const style = window.getComputedStyle(el);

    const isColorMeaningful = (color: string) => {
      return (
        color &&
        !color.includes('rgba(0, 0, 0, 0)') &&
        !color.includes('transparent') &&
        color !== 'rgba(0, 0, 0, 0)'
      );
    };

    return {
      backgroundColor: isColorMeaningful(style.backgroundColor)
        ? style.backgroundColor
        : null,
      textColor: isColorMeaningful(style.color) ? style.color : null,
      fontSize: style.fontSize,
      fontWeight: style.fontWeight !== 'normal' ? style.fontWeight : null,
      fontFamily: style.fontFamily,
      lineHeight: style.lineHeight !== 'normal' ? style.lineHeight : null,
      letterSpacing:
        style.letterSpacing !== 'normal' ? style.letterSpacing : null,
      textTransform:
        style.textTransform !== 'none' ? style.textTransform : null,
      hasBackground: isColorMeaningful(style.backgroundColor),
      hasBorder: style.border !== 'none' && style.borderWidth !== '0px',
      borderRadius: style.borderRadius !== '0px' ? style.borderRadius : null,
      borderColor: isColorMeaningful(style.borderColor)
        ? style.borderColor
        : null,
      borderWidth: style.borderWidth !== '0px' ? style.borderWidth : null,
      boxShadow: style.boxShadow !== 'none' ? style.boxShadow : null,
      opacity: style.opacity !== '1' ? style.opacity : null,
      transform: style.transform !== 'none' ? style.transform : null,
      transition:
        style.transition !== 'all 0s ease 0s' ? style.transition : null,
      cursor: style.cursor,
      display: style.display,
      position: style.position !== 'static' ? style.position : 'static',
      overflow: style.overflow !== 'visible' ? style.overflow : null,
      margin: style.margin !== '0px' ? style.margin : null,
      padding: style.padding !== '0px' ? style.padding : null,
    };
  }, []);

  // Detect design patterns
  const getDesignPattern = useCallback(
    (
      el: Element,
      semanticType: string,
      styling: Record<string, string | null>,
      text: string | null
    ) => {
      const classes = el.className.toLowerCase();
      const lowerText = text?.toLowerCase() || '';

      // CTA patterns
      if (
        lowerText.includes('buy now') ||
        lowerText.includes('get started') ||
        lowerText.includes('sign up') ||
        classes.includes('cta') ||
        classes.includes('primary')
      ) {
        return 'cta';
      }

      // Navigation patterns
      if (
        semanticType === 'navigation' ||
        classes.includes('nav') ||
        classes.includes('menu')
      ) {
        return 'navigation';
      }

      // Form patterns
      if (semanticType === 'input' || el.closest('form')) {
        return 'form-field';
      }

      // Card patterns
      if (
        classes.includes('card') ||
        (styling.hasBorder && styling.borderRadius)
      ) {
        return 'card';
      }

      // Badge patterns
      if (
        classes.includes('badge') ||
        classes.includes('tag') ||
        (styling.borderRadius && el.getBoundingClientRect().height < 40)
      ) {
        return 'badge';
      }

      return 'other';
    },
    []
  );

  // Detect framework classes
  const getFrameworkClasses = useCallback((el: Element) => {
    const classes = Array.from(el.classList);
    const frameworks: string[] = [];

    // Tailwind detection
    if (classes.some((c) => /^(bg-|text-|p-|m-|w-|h-|flex|grid)/.test(c))) {
      frameworks.push('tailwind');
    }

    // Bootstrap detection
    if (classes.some((c) => /^(btn|col-|row|container|navbar)/.test(c))) {
      frameworks.push('bootstrap');
    }

    // Material UI detection
    if (classes.some((c) => /^(MuiButton|MuiTextField|Mui[A-Z])/.test(c))) {
      frameworks.push('material-ui');
    }

    return frameworks;
  }, []);

  // Enhanced form context
  const getFormContext = useCallback((el: Element) => {
    const form = el.closest('form');
    if (!form) return undefined;

    const fieldType = el.getAttribute('type') || el.tagName.toLowerCase();
    const isRequired = el.hasAttribute('required');

    return {
      formMethod: form.getAttribute('method') || 'get',
      formAction: form.getAttribute('action') || '',
      isRequired,
      fieldType,
    };
  }, []);

  // Enhanced list context
  const getListContext = useCallback((el: Element) => {
    const listParent = el.closest('ul, ol, dl');
    if (!listParent) return undefined;

    const listType =
      listParent.tagName.toLowerCase() === 'ul'
        ? 'unordered'
        : listParent.tagName.toLowerCase() === 'ol'
          ? 'ordered'
          : 'definition';

    const items = Array.from(listParent.children);
    const itemIndex = items.indexOf(el as Element) + 1;

    return {
      listType: listType as 'ordered' | 'unordered' | 'definition',
      itemIndex,
      totalItems: items.length,
    };
  }, []);

  // Enhanced nearby elements analysis
  const getEnhancedNearbyElements = useCallback((el: Element) => {
    const nearby: ClarityElementContext['pageContext']['nearbyElements'] = [];
    const rect = el.getBoundingClientRect();

    // Siblings
    if (el.parentElement) {
      Array.from(el.parentElement.children).forEach((sibling) => {
        if (sibling !== el && nearby.length < 5) {
          const siblingRect = sibling.getBoundingClientRect();
          const distance =
            Math.abs(rect.top - siblingRect.top) +
            Math.abs(rect.left - siblingRect.left);

          if (distance < 300) {
            const text = sibling.textContent?.trim();
            nearby.push({
              relationship: 'sibling',
              tag: sibling.tagName.toLowerCase(),
              text: text
                ? text.length > 50
                  ? text.substring(0, 50) + '...'
                  : text
                : null,
              semanticType: sibling.tagName.toLowerCase(),
              distance: Math.round(distance),
            });
          }
        }
      });
    }

    // Parent
    if (el.parentElement && el.parentElement !== document.body) {
      const parentText = el.parentElement.textContent?.trim();
      nearby.push({
        relationship: 'parent',
        tag: el.parentElement.tagName.toLowerCase(),
        text: parentText
          ? parentText.length > 100
            ? parentText.substring(0, 100) + '...'
            : parentText
          : null,
        semanticType: el.parentElement.tagName.toLowerCase(),
      });
    }

    // First meaningful child
    const firstChild = Array.from(el.children).find((child) =>
      child.textContent?.trim()
    );
    if (firstChild) {
      const childText = firstChild.textContent?.trim();
      nearby.push({
        relationship: 'child',
        tag: firstChild.tagName.toLowerCase(),
        text: childText
          ? childText.length > 50
            ? childText.substring(0, 50) + '...'
            : childText
          : null,
        semanticType: firstChild.tagName.toLowerCase(),
      });
    }

    return nearby;
  }, []);

  // Enhanced position analysis
  const getEnhancedPosition = useCallback((el: Element) => {
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const style = window.getComputedStyle(el);

    const viewportPosition =
      rect.top < viewportHeight * 0.33
        ? 'top'
        : rect.top < viewportHeight * 0.66
          ? 'middle'
          : 'bottom';

    const horizontalPosition =
      rect.left < viewportWidth * 0.33
        ? 'left'
        : rect.left < viewportWidth * 0.66
          ? 'center'
          : 'right';

    const isVisible =
      rect.width > 0 &&
      rect.height > 0 &&
      style.visibility !== 'hidden' &&
      style.display !== 'none' &&
      parseFloat(style.opacity) > 0;

    const isAboveFold = rect.top < viewportHeight;
    const zIndex = parseInt(style.zIndex) || 0;

    return {
      x: Math.round(rect.left + window.scrollX),
      y: Math.round(rect.top + window.scrollY),
      viewportPosition,
      horizontalPosition,
      isVisible,
      isAboveFold,
      zIndex,
    };
  }, []);

  // Technical context analysis
  const getTechnicalContext = useCallback(
    (el: Element) => {
      // Check for _listeners property in a type-safe way
      const hasEventListeners =
        ('_listeners' in el &&
          Boolean((el as unknown as { _listeners?: unknown })._listeners)) ||
        el.getAttribute('onclick') !== null ||
        Array.from(el.attributes).some((attr) => attr.name.startsWith('on'));

      const hasAnimation =
        window.getComputedStyle(el).animation !== 'none' ||
        window.getComputedStyle(el).transition !== 'all 0s ease 0s';

      const frameworkClasses = getFrameworkClasses(el);

      // Detect responsive classes
      const classes = Array.from(el.classList);
      const responsiveClass = classes.filter(
        (c) =>
          /^(sm:|md:|lg:|xl:|2xl:)/.test(c) || // Tailwind
          /^(col-|d-|flex-|justify-|align-)/.test(c) // Bootstrap
      );

      const isLazyLoaded =
        el.hasAttribute('loading') && el.getAttribute('loading') === 'lazy';

      return {
        hasEventListeners,
        hasAnimation,
        frameworkClasses,
        responsiveClass,
        isLazyLoaded,
      };
    },
    [getFrameworkClasses]
  );

  // Get clean text content
  const getElementText = useCallback((el: Element): string | null => {
    // Check various text sources
    const ariaLabel = el.getAttribute('aria-label');
    const title = el.getAttribute('title');
    const alt = el.getAttribute('alt');
    const placeholder = el.getAttribute('placeholder');

    if (ariaLabel) return ariaLabel;
    if (alt) return alt;
    if (title) return title;
    if (placeholder) return `Placeholder: ${placeholder}`;

    // Get visible text content (first 150 chars)
    const text = el.textContent?.trim();
    if (text && text.length > 0) {
      return text.length > 150 ? text.substring(0, 150) + '...' : text;
    }

    return null;
  }, []);

  // Determine interaction type
  const getInteractionType = useCallback(
    (el: Element): ClarityElementContext['interactionType'] => {
      const tag = el.tagName.toLowerCase();
      const role = el.getAttribute('role');
      const tabIndex = el.getAttribute('tabindex');
      const style = window.getComputedStyle(el);

      if (
        tag === 'button' ||
        role === 'button' ||
        tag === 'a' ||
        style.cursor === 'pointer'
      ) {
        return 'clickable';
      }
      if (['input', 'select', 'textarea'].includes(tag) || tabIndex !== null) {
        return 'focusable';
      }
      if (role && ['tab', 'menuitem', 'option'].includes(role)) {
        return 'interactive';
      }
      return 'static';
    },
    []
  );

  // Determine element purpose
  const getElementPurpose = useCallback(
    (el: Element, semanticType: string, text: string | null): string => {
      const lowerText = text?.toLowerCase() || '';

      // Business context clues
      if (
        lowerText.includes('buy') ||
        lowerText.includes('purchase') ||
        lowerText.includes('cart')
      ) {
        return 'E-commerce action';
      }
      if (
        lowerText.includes('sign up') ||
        lowerText.includes('register') ||
        lowerText.includes('join')
      ) {
        return 'User registration';
      }
      if (lowerText.includes('login') || lowerText.includes('sign in')) {
        return 'Authentication';
      }
      if (lowerText.includes('contact') || lowerText.includes('support')) {
        return 'Customer support';
      }
      if (lowerText.includes('search')) {
        return 'Search functionality';
      }

      // Semantic purpose
      switch (semanticType) {
        case 'heading':
          return 'Content heading';
        case 'button':
          return 'Action trigger';
        case 'link':
          return 'Navigation';
        case 'input':
          return 'Data input';
        case 'navigation':
          return 'Site navigation';
        case 'text':
          return 'Content display';
        case 'image':
          return 'Visual content';
        default:
          return 'Interface element';
      }
    },
    []
  );

  // Create visual description
  const getVisualDescription = useCallback(
    (el: Element, text: string | null): string => {
      const tag = el.tagName.toLowerCase();
      const classes = Array.from(el.classList).slice(0, 3);
      const id = el.id;

      let description = tag;

      if (id) description += ` with ID "${id}"`;
      if (classes.length > 0)
        description += ` with classes: ${classes.join(', ')}`;
      if (text)
        description += ` containing "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`;

      return description;
    },
    []
  );

  // Categorize size
  const getSizeCategory = useCallback(
    (
      width: number,
      height: number
    ): ClarityElementContext['size']['sizeCategory'] => {
      const area = width * height;
      if (area < 2500) return 'small'; // < 50x50
      if (area < 10000) return 'medium'; // < 100x100
      if (area < 40000) return 'large'; // < 200x200
      return 'extra-large';
    },
    []
  );

  // Find page section context
  const getPageSection = useCallback((el: Element): string | null => {
    let current: Element | null = el;

    while (current && current !== document.body) {
      const tag = current.tagName.toLowerCase();
      const role = current.getAttribute('role');

      if (['main', 'header', 'footer', 'nav', 'aside'].includes(tag)) {
        return tag;
      }
      if (
        role &&
        [
          'main',
          'banner',
          'contentinfo',
          'navigation',
          'complementary',
        ].includes(role)
      ) {
        return role;
      }

      current = current.parentElement;
    }

    return null;
  }, []);

  // Get hierarchy level
  const getHierarchyLevel = useCallback((el: Element): number => {
    let level = 0;
    let current: Element | null = el;

    while (current && current !== document.body) {
      level++;
      current = current.parentElement;
    }

    return level;
  }, []);

  // Determine content type
  const getContentType = useCallback(
    (
      el: Element,
      semanticType: string
    ): ClarityElementContext['pageContext']['contentType'] => {
      const section = getPageSection(el);

      if (
        section === 'nav' ||
        section === 'navigation' ||
        semanticType === 'navigation'
      )
        return 'navigation';
      if (['input', 'button'].includes(semanticType) || el.closest('form'))
        return 'form';
      if (semanticType === 'image') return 'media';
      if (semanticType === 'heading' || semanticType === 'text')
        return 'content';
      return 'decoration';
    },
    [getPageSection]
  );

  // Get user action from element
  const getUserAction = useCallback(
    (el: Element, interactionType: string): string | null => {
      if (interactionType === 'static') return null;

      const text = el.textContent?.toLowerCase() || '';
      const tag = el.tagName.toLowerCase();

      if (text.includes('click') || text.includes('tap')) return 'click';
      if (text.includes('type') || text.includes('enter') || tag === 'input')
        return 'input';
      if (text.includes('select') || text.includes('choose')) return 'select';
      if (text.includes('submit') || text.includes('send')) return 'submit';
      if (text.includes('navigate') || tag === 'a') return 'navigate';

      return interactionType === 'clickable' ? 'click' : 'interact';
    },
    []
  );

  // Determine content category
  const getContentCategory = useCallback(
    (
      el: Element,
      purpose: string
    ): ClarityElementContext['businessContext']['content_category'] => {
      const text = el.textContent?.toLowerCase() || '';

      // Primary actions/content
      if (
        purpose.includes('E-commerce') ||
        purpose.includes('registration') ||
        purpose.includes('Authentication')
      ) {
        return 'primary';
      }
      if (
        text.includes('cta') ||
        text.includes('main') ||
        el.tagName.toLowerCase() === 'h1'
      ) {
        return 'primary';
      }

      // Secondary actions/content
      if (
        purpose.includes('support') ||
        purpose.includes('search') ||
        purpose.includes('navigation')
      ) {
        return 'secondary';
      }

      return 'auxiliary';
    },
    []
  );

  function buildEnhancedContextDescription(
    elementProperties: EnhancedElementProperties
  ): string {
    const { contextualInfo, attributes, position } = elementProperties;
    // Build comprehensive element description
    let context = '\n\nELEMENT ANALYSIS:\n';

    // Basic element info with enhanced attributes
    context += `• Element: ${elementProperties.tagName}`;
    if (elementProperties.id) context += ` with ID "${elementProperties.id}"`;
    if (elementProperties.className) {
      const classes = elementProperties.className
        .split(' ')
        .slice(0, 5)
        .join(', ');
      context += ` with classes: ${classes}`;
    }
    context += '\n';

    // Enhanced attributes
    if (attributes) {
      if (attributes.href)
        context += `• Link destination: ${attributes.href}\n`;
      if (attributes.src)
        context += `• Image/media source: ${attributes.src}\n`;
      if (attributes.alt) context += `• Alt text: "${attributes.alt}"\n`;
      if (attributes.type)
        context += `• Input/button type: ${attributes.type}\n`;
      if (attributes.placeholder)
        context += `• Placeholder: "${attributes.placeholder}"\n`;
      if (attributes.value)
        context += `• Current value: "${attributes.value}"\n`;
      if (attributes.disabled) context += `• Status: Disabled\n`;
      if (attributes.required) context += `• Status: Required field\n`;
      if (attributes.ariaLabel)
        context += `• Accessibility label: "${attributes.ariaLabel}"\n`;

      // Data attributes (show first few)
      if (
        attributes.dataAttributes &&
        Object.keys(attributes.dataAttributes).length > 0
      ) {
        const dataAttrs = Object.entries(attributes.dataAttributes)
          .slice(0, 3)
          .map(([key, value]) => `${key}="${value}"`)
          .join(', ');
        context += `• Data attributes: ${dataAttrs}\n`;
      }
    }

    // Content and purpose
    if (elementProperties.textContent) {
      const text =
        elementProperties.textContent.length > 100
          ? elementProperties.textContent.substring(0, 100) + '...'
          : elementProperties.textContent;
      context += `• Content: "${text}"\n`;
    }

    if (contextualInfo) {
      context += `• Semantic type: ${contextualInfo.semanticType}\n`;
      context += `• Purpose: ${contextualInfo.purpose}\n`;
      context += `• User interaction: ${contextualInfo.interactionType}`;
      if (contextualInfo.userAction) {
        context += ` (expected action: ${contextualInfo.userAction})`;
      }
      context += '\n';

      // Visual characteristics
      context += `• Size: ${position?.width || 'unknown'}×${position?.height || 'unknown'}px (${contextualInfo.sizeCategory})\n`;
      context += `• Position: ${contextualInfo.viewportPosition}`;
      if (position) {
        context += ` at coordinates (${position.x}, ${position.y})`;
      }
      context += '\n';
    }

    // Enhanced styling information
    const styles = elementProperties.computedStyles;
    if (styles) {
      const stylingDetails = [];
      if (styles.backgroundColor && styles.backgroundColor !== 'transparent') {
        stylingDetails.push(`background: ${styles.backgroundColor}`);
      }
      if (styles.color && styles.color !== 'inherit') {
        stylingDetails.push(`text color: ${styles.color}`);
      }
      if (styles.fontSize) {
        stylingDetails.push(`font size: ${styles.fontSize}`);
      }
      if (styles.fontWeight && styles.fontWeight !== 'normal') {
        stylingDetails.push(`font weight: ${styles.fontWeight}`);
      }
      if (styles.borderRadius && styles.borderRadius !== '0px') {
        stylingDetails.push(`border radius: ${styles.borderRadius}`);
      }
      if (styles.display) {
        stylingDetails.push(`display: ${styles.display}`);
      }
      if (styles.position && styles.position !== 'static') {
        stylingDetails.push(`position: ${styles.position}`);
      }

      if (stylingDetails.length > 0) {
        context += `• Current styling: ${stylingDetails.join(', ')}\n`;
      }
    }

    // Accessibility information
    if (contextualInfo && contextualInfo.accessibility) {
      const a11y = contextualInfo.accessibility;
      context += `• Accessibility: `;
      const a11yDetails = [];
      if (a11y.hasAccessibleName) a11yDetails.push('has accessible name');
      if (a11y.isFocusable) a11yDetails.push('focusable');
      if (a11y.ariaExpanded !== undefined)
        a11yDetails.push(
          `expandable (${a11y.ariaExpanded ? 'expanded' : 'collapsed'})`
        );
      context +=
        a11yDetails.length > 0 ? a11yDetails.join(', ') : 'basic accessibility';
      context += '\n';
    }

    if (contextualInfo) {
      // Business and design context
      context += `• Business context: ${contextualInfo.businessContext}\n`;
      context += `• Content priority: ${contextualInfo.contentCategory}\n`;
      if (
        contextualInfo.designPattern &&
        contextualInfo.designPattern !== 'other'
      ) {
        context += `• Design pattern: ${contextualInfo.designPattern}\n`;
      }

      // Form context
      if (contextualInfo.formContext?.isInForm) {
        context += `• Form field: ${contextualInfo.formContext.fieldType}`;
        if (contextualInfo.formContext.isRequired) context += ' (required)';
        context += '\n';
      }

      // Page context
      if (contextualInfo.pageSection) {
        context += `• Page section: ${contextualInfo.pageSection}\n`;
      }

      // Technical notes
      if (
        contextualInfo.technicalNotes &&
        contextualInfo.technicalNotes.length > 0
      ) {
        context += `• Technical notes: ${contextualInfo.technicalNotes.join(', ')}\n`;
      }

      // Nearby elements (enhanced)
      if (
        contextualInfo.nearbyElements &&
        contextualInfo.nearbyElements.length > 0
      ) {
        context += `• Nearby elements:\n`;
        contextualInfo.nearbyElements.slice(0, 4).forEach((element) => {
          context += `  - ${element}\n`;
        });
      }
    }

    return context;
  }

  // Main enhanced analysis function
  const analyzeElementForClarity = useCallback(
    async (selector: string): Promise<ClarityAnalysisData | null> => {
      try {
        const el = await getNearestNode(selector);
        if (!el) return null;

        const rect = el.getBoundingClientRect();
        const semanticType = getSemanticType(el);
        const text = getElementText(el);
        const interactionType = getInteractionType(el);
        const purpose = getElementPurpose(el, semanticType, text);
        const styling = getEnhancedStyling(el);
        const position = getEnhancedPosition(el);

        // Only pass string properties to getDesignPattern
        const stylingForPattern: Record<string, string | null> = {
          backgroundColor: styling.backgroundColor,
          textColor: styling.textColor,
          fontSize: styling.fontSize,
          fontWeight: styling.fontWeight,
          fontFamily: styling.fontFamily,
          lineHeight: styling.lineHeight,
          letterSpacing: styling.letterSpacing,
          textTransform: styling.textTransform,
          borderRadius: styling.borderRadius,
          borderColor: styling.borderColor,
          borderWidth: styling.borderWidth,
          boxShadow: styling.boxShadow,
          opacity: styling.opacity,
          transform: styling.transform,
          transition: styling.transition,
          cursor: styling.cursor,
          display: styling.display,
          position: styling.position,
          overflow: styling.overflow,
          margin: styling.margin,
          padding: styling.padding,
        };

        const elementContext: ClarityElementContext = {
          tag: el.tagName.toLowerCase(),
          role: el.getAttribute('role'),
          semanticType,
          attributes: getElementAttributes(el),
          text,
          visualDescription: getVisualDescription(el, text),
          size: {
            width: Math.round(rect.width),
            height: Math.round(rect.height),
            area: Math.round(rect.width * rect.height),
            sizeCategory: getSizeCategory(rect.width, rect.height),
          },
          position: position as ClarityElementContext['position'],
          interactionType,
          purpose,
          accessibility: getAccessibilityInfo(el),
          styling: styling as ClarityElementContext['styling'],
          pageContext: {
            section: getPageSection(el),
            nearbyElements: getEnhancedNearbyElements(el),
            hierarchyLevel: getHierarchyLevel(el),
            contentType: getContentType(el, semanticType),
            isInForm: !!el.closest('form'),
            formContext: getFormContext(el),
            isInList: !!el.closest('ul, ol, dl'),
            listContext: getListContext(el),
          },
          businessContext: {
            likely_purpose: purpose,
            user_action: getUserAction(el, interactionType),
            content_category: getContentCategory(el, purpose),
            designPattern: getDesignPattern(
              el,
              semanticType,
              stylingForPattern,
              text
            ) as ClarityElementContext['businessContext']['designPattern'],
            brandingElements: Array.from(el.classList).filter(
              (c) =>
                c.includes('brand') ||
                c.includes('logo') ||
                c.includes('primary')
            ),
          },
          technicalContext: getTechnicalContext(el),
        };

        // Enhanced related elements
        const relatedElements = elementContext.pageContext.nearbyElements.map(
          (nearby) => ({
            relationship: nearby.relationship as
              | 'parent'
              | 'sibling'
              | 'child'
              | 'nearby',
            description: `${nearby.tag}${nearby.text ? ` containing "${nearby.text}"` : ''}`,
            element: {
              ...elementContext,
              tag: nearby.tag,
              text: nearby.text,
              semanticType:
                nearby.semanticType as ClarityElementContext['semanticType'],
            },
          })
        );

        return {
          elementContext,
          relatedElements,
          pageMetadata: {
            title: document.title,
            url: window.location.href,
            pageType: document.body.className.includes('home')
              ? 'homepage'
              : 'content page',
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
              devicePixelRatio: window.devicePixelRatio,
            },
            colorScheme: window.matchMedia('(prefers-color-scheme: dark)')
              .matches
              ? 'dark'
              : 'light',
            language: document.documentElement.lang || 'en',
          },
        };
      } catch (error) {
        console.error('Enhanced element context analysis failed:', error);
        return null;
      }
    },
    [
      getSemanticType,
      getElementText,
      getVisualDescription,
      getSizeCategory,
      getInteractionType,
      getElementPurpose,
      getEnhancedStyling,
      getEnhancedPosition,
      getElementAttributes,
      getAccessibilityInfo,
      getPageSection,
      getEnhancedNearbyElements,
      getContentType,
      getHierarchyLevel,
      getFormContext,
      getListContext,
      getContentCategory,
      getUserAction,
      getDesignPattern,
      getTechnicalContext,
    ]
  );

  // Enhanced worker formatting
  const formatForWorker = useCallback(
    (data: ClarityAnalysisData): EnhancedElementProperties => {
      const { elementContext } = data;

      return {
        tagName: elementContext.tag,
        className: elementContext.attributes.className,
        id: elementContext.attributes.id,
        textContent: elementContext.text ?? undefined,
        computedStyles: {
          backgroundColor:
            elementContext.styling.backgroundColor || 'transparent',
          color: elementContext.styling.textColor || 'inherit',
          fontSize: elementContext.styling.fontSize || 'inherit',
          fontWeight: elementContext.styling.fontWeight || 'normal',
          borderRadius: elementContext.styling.borderRadius || '0px',
          display: elementContext.styling.display,
          position: elementContext.styling.position,
        },
        position: {
          x: elementContext.position.x,
          y: elementContext.position.y,
          width: elementContext.size.width,
          height: elementContext.size.height,
        },
        attributes: {
          href: elementContext.attributes.href,
          src: elementContext.attributes.src,
          alt: elementContext.attributes.alt,
          type: elementContext.attributes.type,
          value: elementContext.attributes.value,
          placeholder: elementContext.attributes.placeholder,
          disabled: elementContext.attributes.disabled,
          required: elementContext.attributes.required,
          ariaLabel: elementContext.attributes.ariaLabel,
          dataAttributes: elementContext.attributes.dataAttributes,
        },
        contextualInfo: {
          semanticType: elementContext.semanticType,
          purpose: elementContext.purpose,
          interactionType: elementContext.interactionType,
          pageSection: elementContext.pageContext.section,
          businessContext: elementContext.businessContext.likely_purpose,
          contentCategory: elementContext.businessContext.content_category,
          nearbyElements: elementContext.pageContext.nearbyElements.map(
            (n) =>
              `${n.relationship}: ${n.tag}${n.text ? ` - "${n.text}"` : ''}`
          ),
          userAction: elementContext.businessContext.user_action,
          sizeCategory: elementContext.size.sizeCategory,
          viewportPosition: `${elementContext.position.viewportPosition} ${elementContext.position.horizontalPosition}`,
          accessibility: {
            hasAccessibleName: elementContext.accessibility.hasAccessibleName,
            isFocusable: elementContext.accessibility.isFocusable,
            ariaExpanded: elementContext.accessibility.ariaExpanded,
          },
          formContext: elementContext.pageContext.isInForm
            ? {
                isInForm: true,
                fieldType: elementContext.pageContext.formContext?.fieldType,
                isRequired: elementContext.pageContext.formContext?.isRequired,
              }
            : undefined,
          designPattern: elementContext.businessContext.designPattern,
          technicalNotes: [
            ...(elementContext.technicalContext.frameworkClasses &&
            elementContext.technicalContext.frameworkClasses?.length > 0
              ? [
                  `Uses ${elementContext.technicalContext.frameworkClasses?.join(', ')} framework`,
                ]
              : []),
            ...(elementContext.technicalContext.hasAnimation
              ? ['Has animations/transitions']
              : []),
            ...(elementContext.technicalContext.hasEventListeners
              ? ['Has event listeners']
              : []),
            ...(elementContext.position.isAboveFold
              ? ['Above the fold']
              : ['Below the fold']),
            ...(elementContext.position.isVisible ? [] : ['Not visible']),
          ],
        },
      };
    },
    []
  );

  return {
    analyzeElementForClarity,
    formatForWorker,
    buildEnhancedContextDescription,
  };
};

export default useElementContextAnalyzer;
